import { useMemo } from "react";
import { useRouter } from "next/router";
import festivalApi from "@features/landing/helpers/festivalApi";
import { useLayoutContext } from "@context/layout";
import useRequestImmutable from "@shared/hooks/useRequestImmutable";
import { festivalTemplate } from "../helpers/constants/festival-template";
import { FESTIVAL_TYPE, FestivalProps } from "../types";

export const useLanding = () => {
  const { query, isReady } = useRouter();
  const { isAdmin } = useLayoutContext();

  const slug = useMemo(() => {
    if (!isReady || !query.slug) return undefined;
    return String(query.slug);
  }, [isReady, query.slug]);

  const accessMode = useMemo(() => {
    if (!isReady || !query.access_mode) return undefined;
    return String(query.access_mode);
  }, [isReady, query.access_mode]);

  const shouldFetch = Boolean(slug);

  const { data, error } = useRequestImmutable(
    shouldFetch ? festivalApi.getData(slug!, accessMode) : null,
    { shouldRetryOnError: true }
  );

  const festival = data?.data?.festival;

  const hasError = useMemo(() => {
    if (!shouldFetch) return false;

    return error?.error?.code === 404 || (festival && !festival.is_active);
  }, [error, festival, shouldFetch]);

  const festivalType: FESTIVAL_TYPE = festival?.type ?? FESTIVAL_TYPE.WONDER;
  const FestivalComponent = useMemo(() => {
    return (
      festivalTemplate[festivalType] || festivalTemplate[FESTIVAL_TYPE.WONDER]
    );
  }, [festivalType]) as React.FC<FestivalProps>;

  return {
    FestivalComponent,
    hasError,
    isAdmin,
    data,
  };
};
